import { Swiper, SwiperSlide } from 'swiper/react';
import { A11y, Autoplay, Pagination } from 'swiper';
import 'swiper/swiper-bundle.min.css';
import { testimonials } from '../../data/site';

/*--------------------
* Avatar photo, or initials on a gradient when there isn't one
----------------------*/
function Avatar({ person }) {
  if (person.image) {
    return (
      <img
        className="h-10 w-10 shrink-0 rounded-full object-cover"
        loading="lazy"
        decoding="async"
        width="40"
        height="40"
        src={person.image}
        title={person.name}
        alt={person.name}
      />
    );
  }

  const initials = person.name
    .split(/\s+/)
    .map((part) => part[0])
    .filter((c) => /[A-Za-z]/.test(c))
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <span
      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-brand/70 to-cyan-brand/50 font-display text-[13px] font-[600] text-white"
      aria-hidden="true"
    >
      {initials}
    </span>
  );
}

/*--------------------
* Testimonial Section
----------------------*/
export default function Testimonial() {
  return (
    <section className="section">
      <div className="container">
        <div className="reveal max-w-[720px]">
          <span className="kicker">Client feedback</span>
          <h2 className="section-title mt-4">What it&rsquo;s like to work with me.</h2>
          <p className="lede mt-5">
            Reviews left by clients in the US, Europe, Asia and Africa after delivery.
          </p>
        </div>

        <div className="reveal mt-12 lg:mt-16">
          <Swiper
            className="testimonial-swiper !pb-2"
            spaceBetween={20}
            slidesPerView={1}
            modules={[Pagination, A11y, Autoplay]}
            autoplay={{ delay: 5200, disableOnInteraction: false }}
            pagination={{ clickable: true }}
            breakpoints={{
              768: { slidesPerView: 2 },
              1200: { slidesPerView: 3 },
            }}
          >
            {testimonials.map((t, i) => (
              <SwiperSlide key={`${t.name}-${i}`} className="!h-auto">
                <figure className="glass glass-hover card-sheen flex h-full min-h-[280px] flex-col p-6 lg:p-7">
                  <svg
                    className="h-[22px] w-[29px] text-violet-brand/45"
                    viewBox="0 0 32 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M13.2 0C6.1 2.9 1.6 9 1.6 15.3c0 5 3 8.7 7.2 8.7 3.6 0 6.3-2.7 6.3-6.2 0-3.4-2.4-5.9-5.6-5.9-.6 0-1.4.1-1.6.2-.3 0-.4-.1-.3-.4.9-3 3.4-6 6.6-7.9.4-.2.5-.5.3-.9L13.9.3c-.2-.3-.4-.4-.7-.3Zm16 0C22.1 2.9 17.6 9 17.6 15.3c0 5 3 8.7 7.2 8.7 3.6 0 6.3-2.7 6.3-6.2 0-3.4-2.4-5.9-5.6-5.9-.6 0-1.4.1-1.6.2-.3 0-.4-.1-.3-.4.9-3 3.4-6 6.6-7.9.4-.2.5-.5.3-.9L29.9.3c-.2-.3-.4-.4-.7-.3Z" />
                  </svg>

                  <blockquote className="mt-2 flex-1 text-[14.5px] leading-[1.72] text-muted">
                    {t.quote}
                  </blockquote>

                  <figcaption className="mt-6 flex items-center gap-3 border-t border-tint/[.07] pt-5">
                    <Avatar person={t} />
                    <div className="min-w-0">
                      <div className="font-display text-[15px] font-[600] text-chalk">
                        {t.name}
                        {t.company && (
                          <span className="font-sans font-[400] text-muted">
                            {' · '}
                            {t.company}
                          </span>
                        )}
                      </div>
                      <div className="font-mono text-[11px] uppercase tracking-[.14em] text-tint/50">
                        {t.place}
                      </div>
                    </div>
                  </figcaption>
                </figure>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
}
